import type { z } from 'zod';

export interface WindowLike {
  postMessage(message: unknown, targetOrigin: string): void;
  addEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
  removeEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
}

type PayloadOf<In extends { type: string }, T extends In['type']> = Extract<In, { type: T }> extends {
  payload: infer P;
}
  ? P
  : undefined;

export interface MessageBridgeOptions {
  /** Ожидаемый origin второй стороны. Всё остальное отбрасывается. */
  peerOrigin: string;
  listenOn?: WindowLike;
  /** Дополнительная проверка источника (например, event.source === iframe.contentWindow). */
  sourceCheck?: (event: MessageEvent) => boolean;
  onInvalid?: (data: unknown) => void;
}

/**
 * Двусторонний мост хост ↔ виджет. Работает только через postMessage,
 * origin строго проверяется: токен гостя никогда не попадает в URL.
 */
export class MessageBridge<Out extends { type: string }, In extends { type: string }> {
  readonly #handlers = new Map<string, Set<(payload: unknown) => void>>();
  readonly #schema: z.ZodType<In>;
  readonly #peerOrigin: string;
  readonly #target: WindowLike;
  readonly #listenOn: WindowLike;
  readonly #sourceCheck: ((event: MessageEvent) => boolean) | undefined;
  readonly #onInvalid: ((data: unknown) => void) | undefined;
  #listening = false;

  constructor(
    incomingSchema: z.ZodType<In>,
    options: MessageBridgeOptions & { target: WindowLike },
  ) {
    this.#schema = incomingSchema;
    this.#peerOrigin = options.peerOrigin;
    this.#target = options.target;
    this.#listenOn = options.listenOn ?? (globalThis.window as unknown as WindowLike);
    this.#sourceCheck = options.sourceCheck;
    this.#onInvalid = options.onInvalid;
  }

  send(message: Out): void {
    this.#target.postMessage(message, this.#peerOrigin);
  }

  on<T extends In['type']>(
    type: T,
    handler: (payload: PayloadOf<In, T>, message: In) => void,
  ): () => void {
    let bucket = this.#handlers.get(type);
    if (!bucket) {
      bucket = new Set();
      this.#handlers.set(type, bucket);
    }
    bucket.add(handler as (payload: unknown) => void);
    return () => bucket.delete(handler as (payload: unknown) => void);
  }

  start(): () => void {
    if (this.#listening) return () => this.stop();
    this.#listenOn.addEventListener('message', this.#handleMessage);
    this.#listening = true;
    return () => this.stop();
  }

  stop(): void {
    if (!this.#listening) return;
    this.#listenOn.removeEventListener('message', this.#handleMessage);
    this.#listening = false;
  }

  #handleMessage = (event: MessageEvent): void => {
    if (event.origin !== this.#peerOrigin) return;
    if (this.#sourceCheck && !this.#sourceCheck(event)) return;
    const parsed = this.#schema.safeParse(event.data);
    if (!parsed.success) {
      this.#onInvalid?.(event.data);
      return;
    }
    const message = parsed.data;
    const bucket = this.#handlers.get(message.type);
    if (!bucket) return;
    for (const handler of bucket) {
      handler((message as { payload?: unknown }).payload);
    }
  };
}
